/**
 * perfOverlay — @yjsgames/perf 조립 앱 어댑터.
 *
 * perfStats + PhaserPerfSource + PerfProbe + 오버레이 뷰 + 렉 모니터 + 임계 watch를 한 도구(PerfTool)로 묶는다.
 * GameScene이 update()에서 probe.section()으로 구간을 감싸고 flush()로 수집+갱신+로그한다.
 * counts(객체 수)는 게임 도메인이라 GameScene이 콜백으로 주입 — 패키지는 도메인 무지.
 *
 * 토글: 치트 모달 / AutoPlay TOGGLE_PERF_OVERLAY. 기본값은 Off이며 저장된 사용자 설정을 따른다.
 * 렉/저메모리 로그는 enabled일 때만(off면 flush 전체 no-op). native 파일 캡처 비용을 피하려고
 * @yjsgames/perf reporter의 info 채널(logcat 중심)을 쓴다.
 */
import type Phaser from "phaser";
import {
  createPerfStats,
  createPerfProbe,
  createPhaserPerfSource,
  createPerfOverlay,
  createLagMonitor,
  createPerfWatch,
  createPerfConsoleReporter,
  createThrottledPerfCounts,
  createBrowserTimingMonitor,
  type PerfProbe,
  type PerfSnapshot,
} from "@yjsgames/perf";
import { PERF_BROWSER_TIMING_ENABLED } from "../env/appFlavor.js";

export interface PerfTool {
  /** update()에서 구간 계측 — probe.section("collide", () => runner.update()). */
  readonly probe: PerfProbe;
  setEnabled(on: boolean): void;
  /** 토글 후 새 상태 반환. */
  toggle(): boolean;
  isEnabled(): boolean;
  /** update() 말미 1회 — 프레임 지표 수집 + 오버레이 갱신 + 렉/임계 로그(off면 no-op). */
  flush(): void;
  /** 현재 지표를 콘솔(warn 채널 = AutoPlay 캡처)로 출력 — DUMP_PERF_STATS. */
  dump(): void;
  destroy(): void;
}

const fmtSections = (s: Record<string, number>): string =>
  Object.entries(s)
    .map(([k, v]) => `${k} ${v.toFixed(1)}`)
    .join(" ");
const fmtCounts = (c: Record<string, number>): string =>
  Object.entries(c)
    .map(([k, v]) => `${k} ${v}`)
    .join(" ");
const fmtOverlay = (s: PerfSnapshot): string[] => [
  `FPS ${s.fps.toFixed(0)} low ${s.lowFps.toFixed(0)} ${s.frameMs.toFixed(0)}ms`,
  `obj ${s.counts.displayRenderable ?? 0}/${s.counts.display ?? 0} txt ${s.counts.textRenderable ?? 0} img ${s.counts.imageRenderable ?? 0} gfx ${s.counts.graphicsRenderable ?? 0}`,
  `hud ${(s.sections.hud ?? 0).toFixed(1)} perf ${(s.sections.perfFlush ?? 0).toFixed(1)}`,
];

export function createPerfTool(
  scene: Phaser.Scene,
  game: Phaser.Game,
  counts: () => Record<string, number>,
  initialEnabled = false,
): PerfTool {
  const stats = createPerfStats();
  const timing = PERF_BROWSER_TIMING_ENABLED
    ? createBrowserTimingMonitor({ intervalMs: 1000, lagThresholdMs: 30 })
    : null;
  const source = createPhaserPerfSource(
    game,
    createThrottledPerfCounts(() => ({ ...counts(), ...(timing?.sample() ?? {}) }), { intervalMs: 500 }),
  );
  const probe = createPerfProbe(stats, source);
  const overlay = createPerfOverlay(scene, {
    name: "perfOverlayText",
    x: 8,
    y: 108,
    fontSize: 9,
    updateIntervalMs: 1000,
  });
  const log = createPerfConsoleReporter({ level: "info", minIntervalMs: 3000 });
  const sampleLog = createPerfConsoleReporter({ level: "info" });
  let nextSampleLogAt = 0;

  // 렉 스파이크(>50ms=20fps↓) 자동 로그 — debounce 1초로 쏟아짐 억제.
  const lag = createLagMonitor({
    lagThresholdMs: 50,
    debounceMs: 1000,
    onLag: (c) =>
      log.report(
        `[perf][lag] ${c.frameMs.toFixed(0)}ms (fps ${c.fps.toFixed(0)}) | ${fmtSections(c.sections)} | ${fmtCounts(c.counts)}`,
      ),
  });
  // 주기(5초) 임계 샘플링 — 지속 저fps / 저메모리 임박 경고.
  const watch = createPerfWatch({
    sampleMs: 5000,
    lowFps: 40,
    highHeapRatio: 0.85,
    onWarn: (c) =>
      c.kind === "lowFps"
        ? log.report(`[perf][lowFps] 지속 저fps ${c.value.toFixed(0)} (5초 샘플)`)
        : log.report(
            `[perf][highHeap] 저메모리 임박 ${(c.value * 100).toFixed(0)}% (${c.heapMB?.toFixed(0)}/${c.heapLimitMB?.toFixed(0)}MB)`,
          ),
  });

  function setEnabled(on: boolean): void {
    probe.enabled = on;
    overlay.setEnabled(on);
    timing?.setEnabled(on);
  }
  setEnabled(initialEnabled);

  return {
    probe,
    setEnabled,
    toggle(): boolean {
      const next = !probe.enabled;
      setEnabled(next);
      return next;
    },
    isEnabled: () => probe.enabled,
    flush(): void {
      const sample = probe.tick(); // enabled일 때만 source.sample → stats.frame, raw 샘플 반환(off면 null)
      if (sample === null) return; // off면 계측·로그 전체 비용 0
      const now = performance.now();
      overlay.renderLazy(() => fmtOverlay(stats.snapshot()));
      if (now >= nextSampleLogAt) {
        nextSampleLogAt = now + 5000;
        const snap = stats.snapshot();
        sampleLog.report(
          `[perf][sample] ${sample.frameMs.toFixed(0)}ms (fps ${sample.fps.toFixed(0)}, avg ${snap.fps.toFixed(0)}) | ${fmtSections(snap.sections)} | ${fmtCounts(sample.counts)}`,
        );
      }
      // 렉 컨텍스트는 lazy — 렉이 아니면 평가 안 됨(평시 비용 0).
      lag.observe(sample.frameMs, () => ({
        fps: sample.fps,
        sections: stats.snapshot().sections,
        counts: sample.counts,
      }));
      watch.tick(sample.frameMs, {
        fps: sample.fps,
        heapMB: sample.heapMB,
        heapLimitMB: sample.heapLimitMB,
      });
    },
    dump(): void {
      stats.frame(source.sample());
      log.report(`[perf] ${stats.format().join(" | ")}`);
    },
    destroy(): void {
      timing?.destroy();
      overlay.destroy();
    },
  };
}
